import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/** @type {string} */
export const CART_DRAWER_SYNCED_EVENT = 'cart-drawer:synced';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.3;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
    document.addEventListener('cart:morphed', this.#handleCartChange);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
    document.removeEventListener('cart:morphed', this.#handleCartChange);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Sync drawer empty-state after cart section morphing (e.g. first add from empty cart).
   * Uses double rAF so DOM updates from morphSection finish before we read #cart-form.
   */
  #handleCartChange = () => {
    this.#scheduleDrawerSync();
  };

  #scheduleDrawerSync() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncEmptyState();
        this.#updateStickyState();
        document.dispatchEvent(new CustomEvent(CART_DRAWER_SYNCED_EVENT, { bubbles: true }));
      });
    });
  }

  #syncEmptyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const hasItems = Boolean(dialog.querySelector('#cart-form'));
    dialog.classList.toggle('cart-drawer--empty', !hasItems);
    dialog.setAttribute('aria-labelledby', hasItems ? 'cart-drawer-heading' : 'cart-drawer-heading-empty');
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #handleDialogOpen = () => {
    this.#scheduleDrawerSync();
    this.#focusDrawerOnOpen();
  };

  /**
   * Move focus off the close control when the modal opens (showModal focuses the first
   * focusable element, which is usually Close). Heading keeps screen reader context.
   */
  #focusDrawerOnOpen() {
    requestAnimationFrame(() => {
      const { dialog } = /** @type {Refs} */ (this.refs);
      if (!dialog?.open) return;

      const focusTarget = dialog.querySelector('.cart-drawer__heading');
      if (!(focusTarget instanceof HTMLElement)) return;

      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    });
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
